import type { ForwardedRef } from 'react';
import { forwardRef } from 'react';
import type { AnimationControls, MotionProps } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import { useMeQuery } from '@frameio/graphql-client/generated';
import { useIsElementAtTopOfViewWithScrollEvent } from '@frameio/shared/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { adobeManagedGroupIsSyncing } from '@frameio/shared/userGroups/utils/adobeManagedGroupIsSyncing';
import { Box, Text, useId } from '@frameio/vapor';
import type { GetDirectMembershipsResource } from '../../../hooks/useGetDirectMemberships';
import { StyledDivForTitle, StyledList } from '../styles';
import type { InviteAccessMemberProps } from '../types';
import { InviteAccessMember } from './InviteAccessMember';
import { InviteAccessMemberRowSkeletonsForList } from './InviteAccessMemberRowSkeletonsForList';
import { InviteAccessMemberWithMembers } from './InviteAccessMemberWithMembers';

type InheritedMembers = NonNullable<
  GetDirectMembershipsResource['indirectMemberships']
>;
type DirectMembers = NonNullable<
  GetDirectMembershipsResource['directMemberships']
>;
type AccessMembers = (InheritedMembers | DirectMembers)['nodes'];

type InviteAccessMemberListProps = {
  accessCanBeManaged?: boolean;
  accessMembers: AccessMembers;
  animateControls?: AnimationControls;
  disableStickyHeader?: boolean;
  initialRowStyle?: MotionProps['initial'];
  placeholderCount?: number;
  resourceId: string;
  resourceType: string;
  title?: string;
  trackingSource?: TrackingSourceType;
} & Pick<
  InviteAccessMemberProps,
  | 'availableRoles'
  | 'createMemberships'
  | 'deleteMembership'
  | 'queryText'
  | 'backgroundColor'
>;

const isInheritedMember = (
  accessMember: AccessMembers[number]
): accessMember is InheritedMembers['nodes'][number] =>
  'grantingResource' in accessMember;

export const InviteAccessMemberList = forwardRef(
  (
    {
      accessCanBeManaged = false,
      accessMembers,
      animateControls,
      availableRoles,
      createMemberships,
      deleteMembership,
      disableStickyHeader,
      initialRowStyle,
      queryText,
      backgroundColor,
      placeholderCount = 0,
      title,
      resourceId,
      resourceType,
      trackingSource,
    }: InviteAccessMemberListProps,
    scrollRef: ForwardedRef<HTMLElement>
  ) => {
    const scrollRefCurrent =
      scrollRef && typeof scrollRef !== 'function' ? scrollRef.current : null;
    const [titleRef, isScrolledToTop] =
      useIsElementAtTopOfViewWithScrollEvent(scrollRefCurrent);

    const { data: currentUser } = useMeQuery();
    const titleId = useId();

    return (
      <>
        {title && (
          <StyledDivForTitle
            ref={titleRef}
            $backgroundColor={backgroundColor}
            $showShadow={isScrolledToTop && !disableStickyHeader}
          >
            <Text color="tertiary" id={titleId} variant="body">
              {title}
            </Text>
          </StyledDivForTitle>
        )}
        <Box
          aria-labelledby={titleId}
          as={StyledList}
          paddingTop={title ? 4 : undefined}
          paddingX={16}
          width="100%"
        >
          <AnimatePresence initial={false}>
            {accessMembers.map((accessMember: AccessMembers[number]) => {
              const isAccessMemberWithMembers = accessMember.type === 'group';
              const isGroupMember = accessMember.type === 'group_member';

              const Component = isAccessMemberWithMembers
                ? InviteAccessMemberWithMembers
                : InviteAccessMember;

              return (
                <motion.li
                  key={
                    accessMember.accountUser?.id ||
                    accessMember.accountUserGroup?.id
                  }
                  animate={animateControls}
                  custom={accessMember.id}
                  initial={initialRowStyle}
                >
                  <Component
                    accessCanBeManaged={
                      !isGroupMember &&
                      !isInheritedMember(accessMember) &&
                      accessCanBeManaged
                    }
                    availableRoles={availableRoles}
                    backgroundColor={backgroundColor}
                    createMemberships={createMemberships}
                    currentUserId={currentUser?.me?.id}
                    deleteMembership={deleteMembership}
                    isSyncing={
                      isAccessMemberWithMembers &&
                      accessMember?.userGroup?.__typename ===
                        'AdobeManagedUserGroup' &&
                      adobeManagedGroupIsSyncing(
                        accessMember.userGroup.syncStatus
                      )
                    }
                    memberCount={
                      isAccessMemberWithMembers
                        ? accessMember?.accountUserGroup?.memberCount ??
                          undefined
                        : undefined
                    }
                    membershipId={accessMember.id}
                    queryText={queryText}
                    resourceId={resourceId}
                    resourceType={resourceType}
                    trackingSource={trackingSource}
                  />
                </motion.li>
              );
            })}
          </AnimatePresence>
        </Box>
        <InviteAccessMemberRowSkeletonsForList count={placeholderCount} />
      </>
    );
  }
);
